import * as React from "react";
import { FormGroup, FormControlLabel, Checkbox, FormControl, InputLabel, Select, MenuItem, TextField } from "@mui/material";
import type { StepConfig } from "./config";
import { storageKeyFor, storageSetJSON, storageRemoveKey } from "./investigationToolLocal";

export type AnswersState = Record<number, Record<string, boolean | string>>;

// Sets user selections in localStorage and state
export function createSetAnswer(params: {
  businessIdOrName: string;
  scopeKey: string;
  setAnswers: React.Dispatch<React.SetStateAction<AnswersState>>;
}) {
  const { businessIdOrName, scopeKey, setAnswers } = params;

  return (step: number, qid: string, value: boolean | string) => {
    const key = storageKeyFor(businessIdOrName, scopeKey, step, qid);

    if (value === "" || value === false) storageRemoveKey(key);
    else storageSetJSON(key, value);

    setAnswers((prev) => ({
      ...prev,
      [step]: { ...(prev[step] || {}), [qid]: value },
    }));
  };
}

// Renders all questions for a given step
export function StepBoxes(props: {
  step: number;
  stepsConfig: StepConfig[];
  answers: AnswersState;
  onChange: (step: number, qid: string, value: boolean | string) => void;
}) {
  const { step, stepsConfig, answers, onChange } = props;
  const cfg = stepsConfig[step];
  if (!cfg) return null;

  const stepAnswers = answers[step] || {};

  return (
    <FormGroup>
      {cfg.questions.map((question) => {
        if (question.type === "checkbox") {
          const checked = Boolean(stepAnswers[question.id]);
          return (
            <FormControlLabel
              key={question.id}
              control={<Checkbox checked={checked} onChange={(_, val) => onChange(step, question.id, val)} />}
              label={question.label}
            />
          );
        }

        if (question.type === "select") {
          const value = typeof stepAnswers[question.id] === "string" ? (stepAnswers[question.id] as string) : "";
          return (
            <FormControl key={question.id} sx={{ mt: 1.5, maxWidth: 420 }}>
              <InputLabel id={`${question.id}-label`}>{question.label}</InputLabel>
              <Select
                labelId={`${question.id}-label`}
                label={question.label}
                value={value}
                onChange={(e) => onChange(step, question.id, String(e.target.value))}
              >
                {(question.options ?? []).map((opt) => (
                  <MenuItem key={opt} value={opt}>
                    {opt}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          );
        }

        if (question.type === "textarea") {
          const value = typeof stepAnswers[question.id] === "string" ? (stepAnswers[question.id] as string) : "";
          return (
            <TextField
              key={question.id}
              label={question.label}
              value={value}
              onChange={(e) => onChange(step, question.id, e.target.value)}
              multiline
              rows={3}
              variant="outlined"
              sx={{ mt: 1.5, maxWidth: 600 }}
            />
          );
        }

        return null;
      })}
    </FormGroup>
  );
}
